import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import axios from 'axios';
import url from '../../url.js';
import ActivityAnalysisCharts from './ActivityAnalysisCharts';
import UserSpeakingHabitsTable from './UserSpeakingHabitsTable';

/**
 * 小組儀表板組件 - 使用模組化的圖表和表格組件
 * 
 * @param {string} activityId - 活動 ID
 * @param {string} groupId - 小組 ID
 * @param {function} onDataLoaded - 數據載入完成後的回調（可選）
 */
const GroupDashboard = ({ activityId, groupId, onDataLoaded }) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activityId || !groupId) return;

    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const jwtToken = localStorage.getItem('jwtToken');
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};

        // 獲取小組節點
        const groupResponse = await axios.get(
          `${url.backendHost}api/nodes/all/${groupId}`,
          { headers, withCredentials: true }
        );

        const groupData = groupResponse.data?.[0] || {};
        const nodesData = groupData.Nodes || [];
        const edgesData = groupData.Edges || [];
        setNodes(nodesData);
        setEdges(edgesData);
        
        if (onDataLoaded) {
          onDataLoaded({ nodes: nodesData, edges: edgesData });
        }
      } catch (error) {
        console.error('載入儀表板數據失敗:', error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [activityId, groupId, onDataLoaded]);

  // 載入中狀態
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} sx={{ mr: 1.5 }} />
        <Typography variant="body2" color="text.secondary">
          載入儀表板數據中...
        </Typography>
      </Box>
    );
  }

  // 錯誤狀態
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <DashboardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          無法載入儀表板數據
        </Typography>
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error.message || '未知錯誤'}
        </Typography>
      </Box>
    );
  }

  // 無數據狀態
  if (nodes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <DashboardIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          目前沒有節點數據
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2.5,
      width: '100%'
    }}>
      <ActivityAnalysisCharts nodes={nodes} visible={true} />
      <UserSpeakingHabitsTable nodes={nodes} edges={edges} />
    </Box>
  );
};

export default GroupDashboard;
